import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BedDouble, Building, Tag, User, Wallet } from "lucide-react";
import { getAccommodationsByTournament } from "../../api/accommodations";
import { IAccommodation } from "../../models/IAccommodation";
import moment from "moment";
import {
  Card,
  DescriptionList,
  Dialog,
  EmptyState,
  Loader,
  PageHeader,
  SearchInput,
  Segmented,
  StatCard,
  Tag as StatusTag,
  TagTone,
  Toolbar,
} from "../../ui";
import "./styles.scss";

// The "All" segment of the room type filter (selectedRoomType stays null for it)
const ALL_ROOM_TYPES = "__all__";

// Payment status pill colours, as before: paid green, pending amber, failed red
const paymentStatusTone = (status: string): TagTone => {
  switch (status.toLowerCase()) {
    case "paid":
      return "ok";
    case "pending":
      return "warn";
    case "failed":
      return "danger";
    default:
      return "neutral";
  }
};

const TournamentAccommodations = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const hasFetched = useRef(false);

  const [loading, setLoading] = useState(true);
  const [accommodations, setAccommodations] = useState<IAccommodation[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRoomType, setSelectedRoomType] = useState<string | null>(null);
  const [selectedAccommodation, setSelectedAccommodation] = useState<IAccommodation | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const getAccommodationsData = async () => {
    setLoading(true);
    try {
      const res = await getAccommodationsByTournament(id!);
      setAccommodations(res.data || []);
    } catch (error: any) {
      console.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    getAccommodationsData();
  }, []);

  // Calculate room type counts
  const roomTypeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    accommodations.forEach((accommodation) => {
      const roomType = accommodation.accommodationDetails.roomTypeName;
      counts[roomType] = (counts[roomType] || 0) + 1;
    });
    return counts;
  }, [accommodations]);

  // Get unique room types sorted by name
  const roomTypes = useMemo(() => {
    return Object.keys(roomTypeCounts).sort();
  }, [roomTypeCounts]);

  const filteredAccommodations = accommodations.filter((accommodation) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      accommodation.playerInfo.firstName.toLowerCase().includes(searchLower) ||
      accommodation.playerInfo.lastName.toLowerCase().includes(searchLower) ||
      accommodation.playerInfo.email.toLowerCase().includes(searchLower) ||
      accommodation.teamInfo.teamName.toLowerCase().includes(searchLower) ||
      accommodation.accommodationDetails.roomTypeName.toLowerCase().includes(searchLower);

    const matchesRoomType = selectedRoomType === null || accommodation.accommodationDetails.roomTypeName === selectedRoomType;

    return matchesSearch && matchesRoomType;
  });

  const totalRooms = accommodations.reduce((sum, acc) => sum + acc.accommodationDetails.numberOfRooms, 0);
  const totalRevenue = accommodations.reduce((sum, acc) => sum + acc.pricing.totalAfterDiscount, 0);
  const currency = accommodations.length > 0 ? accommodations[0].pricing.currency : "";

  const handleCardClick = (accommodation: IAccommodation) => {
    setSelectedAccommodation(accommodation);
    setIsDialogOpen(true);
  };

  // The kit Dialog plays the closing animation; the booking stays selected until the next one is opened so the
  // dialog keeps its content while it animates out.
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  return (
    <div className="tournament-accommodations-page">
      <PageHeader
        back={{ onClick: () => navigate(-1), label: "Back to Tournament" }}
        title="Accommodations"
        subtitle="View all accommodation bookings for this tournament"
      />

      <div className="accommodations-stats">
        <StatCard label="Total Bookings" value={accommodations.length} icon={<Building />} />
        <StatCard label="Total Rooms" value={totalRooms} icon={<BedDouble />} tone="brand" />
        <StatCard
          label="Total Revenue"
          value={
            <>
              {currency} {totalRevenue.toLocaleString()}
            </>
          }
          icon={<Wallet />}
          tone="ok"
        />
      </div>

      <Toolbar>
        <SearchInput
          className="accommodations-search"
          placeholder="Search by player name, email, team, or room type..."
          value={searchTerm}
          onChange={setSearchTerm}
        />
        {roomTypes.length > 0 && (
          <Segmented
            ariaLabel="Room type"
            value={selectedRoomType ?? ALL_ROOM_TYPES}
            onChange={(value) => setSelectedRoomType(value === ALL_ROOM_TYPES ? null : value)}
            options={[
              { value: ALL_ROOM_TYPES, label: "All", count: accommodations.length },
              ...roomTypes.map((roomType) => ({ value: roomType, label: roomType, count: roomTypeCounts[roomType] })),
            ]}
          />
        )}
      </Toolbar>

      {loading ? (
        <Loader minHeight={300} />
      ) : filteredAccommodations.length === 0 ? (
        <Card padding="none">
          <EmptyState
            icon={<Building />}
            title="No accommodations found"
            description={searchTerm ? "Try adjusting your search" : "No accommodation bookings have been made yet"}
          />
        </Card>
      ) : (
        <div className="accommodations-list">
          {filteredAccommodations.map((accommodation) => {
            return (
              <Card
                key={accommodation.id}
                className="accommodation-card"
                role="button"
                tabIndex={0}
                onClick={() => handleCardClick(accommodation)}
                onKeyDown={(e) => {
                  if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    handleCardClick(accommodation);
                  }
                }}
                icon={<User />}
                title={
                  <>
                    {accommodation.playerInfo.firstName} {accommodation.playerInfo.lastName}
                    {accommodation.playerInfo.nickname && ` (${accommodation.playerInfo.nickname})`}
                  </>
                }
                subtitle={
                  <>
                    {accommodation.teamInfo.teamName} • {accommodation.accommodationDetails.roomTypeName}
                  </>
                }
                actions={
                  <StatusTag tone={paymentStatusTone(accommodation.teamMemberFeeDetails.paymentStatus)}>
                    {accommodation.teamMemberFeeDetails.paymentStatus}
                  </StatusTag>
                }
                footer={
                  <p className="accommodation-card__booked">
                    Booked on {moment(accommodation.createdAt).format("DD MMM YYYY, HH:mm")}
                  </p>
                }
              >
                <DescriptionList
                  columns={3}
                  items={[
                    {
                      label: "Check-in",
                      value: moment(accommodation.accommodationDetails.checkInDate).format("DD MMM YYYY"),
                    },
                    {
                      label: "Check-out",
                      value: moment(accommodation.accommodationDetails.checkOutDate).format("DD MMM YYYY"),
                    },
                    {
                      label: "Total",
                      value: (
                        <span className="accommodation-card__total">
                          {accommodation.pricing.currency} {accommodation.pricing.totalAfterDiscount.toLocaleString()}
                        </span>
                      ),
                    },
                  ]}
                />
              </Card>
            );
          })}
        </div>
      )}

      <Dialog
        open={isDialogOpen && !!selectedAccommodation}
        onClose={handleCloseDialog}
        size="lg"
        icon={<User />}
        title={
          selectedAccommodation && (
            <>
              {selectedAccommodation.playerInfo.firstName} {selectedAccommodation.playerInfo.lastName}
              {selectedAccommodation.playerInfo.nickname && ` (${selectedAccommodation.playerInfo.nickname})`}
            </>
          )
        }
        subtitle={selectedAccommodation?.teamInfo.teamName}
      >
        {selectedAccommodation && (
          <div className="accommodation-detail">
            <section className="accommodation-detail__section">
              <h3 className="accommodation-detail__title">Player Information</h3>
              <DescriptionList
                items={[
                  { label: "Email", value: selectedAccommodation.playerInfo.email },
                  { label: "Phone", value: selectedAccommodation.playerInfo.phoneNumber },
                  { label: "Category", value: selectedAccommodation.teamInfo.categoryName },
                  {
                    label: "Payment Status",
                    value: (
                      <StatusTag tone={paymentStatusTone(selectedAccommodation.teamMemberFeeDetails.paymentStatus)}>
                        {selectedAccommodation.teamMemberFeeDetails.paymentStatus}
                      </StatusTag>
                    ),
                  },
                ]}
              />
            </section>

            <section className="accommodation-detail__section">
              <h3 className="accommodation-detail__title">Accommodation Details</h3>
              <DescriptionList
                items={[
                  { label: "Room Type", value: selectedAccommodation.accommodationDetails.roomTypeName },
                  { label: "Number of Rooms", value: selectedAccommodation.accommodationDetails.numberOfRooms },
                  {
                    label: "Check-in",
                    value: moment(selectedAccommodation.accommodationDetails.checkInDate).format("DD MMM YYYY"),
                  },
                  {
                    label: "Check-out",
                    value: moment(selectedAccommodation.accommodationDetails.checkOutDate).format("DD MMM YYYY"),
                  },
                  { label: "Total Nights", value: selectedAccommodation.accommodationDetails.totalNights },
                ]}
              />
            </section>

            {selectedAccommodation.roomsList && selectedAccommodation.roomsList.length > 0 && (
              <section className="accommodation-detail__section">
                <h3 className="accommodation-detail__title">Rooms</h3>
                <div className="accommodation-rooms">
                  {selectedAccommodation.roomsList.map((room, idx) => (
                    <div key={idx} className="accommodation-room">
                      <p className="accommodation-room__number">Room {room.roomNumber}</p>
                      <p className="accommodation-room__type">{room.roomType}</p>
                      {room.features && room.features.length > 0 && (
                        <div className="accommodation-room__features">
                          {room.features.map((feature, fIdx) => (
                            <StatusTag key={fIdx} tone="brand">
                              {feature}
                            </StatusTag>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {selectedAccommodation.accommodationDetails.specialRequests && (
              <section className="accommodation-detail__section">
                <h3 className="accommodation-detail__title">Special Requests</h3>
                <p className="accommodation-detail__requests">
                  {selectedAccommodation.accommodationDetails.specialRequests}
                </p>
              </section>
            )}

            <section className="accommodation-detail__section">
              <h3 className="accommodation-detail__title">Pricing Details</h3>
              <div className="accommodation-pricing">
                <div className="accommodation-pricing__row">
                  <p className="accommodation-pricing__label">
                    Base Package ({selectedAccommodation.pricing.basePackageNights} nights)
                  </p>
                  <p className="accommodation-pricing__value">
                    {selectedAccommodation.pricing.currency}{" "}
                    {selectedAccommodation.pricing.basePackageFee.toLocaleString()}
                  </p>
                </div>
                {selectedAccommodation.pricing.extraNights > 0 && (
                  <div className="accommodation-pricing__row">
                    <p className="accommodation-pricing__label">
                      Extra Nights ({selectedAccommodation.pricing.extraNights} ×{" "}
                      {selectedAccommodation.pricing.currency} {selectedAccommodation.pricing.extraNightFee})
                    </p>
                    <p className="accommodation-pricing__value">
                      {selectedAccommodation.pricing.currency}{" "}
                      {selectedAccommodation.pricing.extraNightsTotalFee.toLocaleString()}
                    </p>
                  </div>
                )}
                <div className="accommodation-pricing__row">
                  <p className="accommodation-pricing__label">Subtotal per Room</p>
                  <p className="accommodation-pricing__value">
                    {selectedAccommodation.pricing.currency}{" "}
                    {selectedAccommodation.pricing.subtotalPerRoom.toLocaleString()}
                  </p>
                </div>
                <div className="accommodation-pricing__row">
                  <p className="accommodation-pricing__label">
                    Total for {selectedAccommodation.pricing.numberOfRooms} room(s)
                  </p>
                  <p className="accommodation-pricing__value">
                    {selectedAccommodation.pricing.currency}{" "}
                    {selectedAccommodation.pricing.subtotalAllRooms.toLocaleString()}
                  </p>
                </div>
                {selectedAccommodation.pricing.promoCodeApplied && (
                  <div className="accommodation-pricing__row accommodation-pricing__row--promo">
                    <div className="accommodation-pricing__promo">
                      <Tag size={16} aria-hidden="true" />
                      <p className="accommodation-pricing__label">
                        Promo Code: {selectedAccommodation.pricing.promoCodeApplied.code}
                        {selectedAccommodation.pricing.promoCodeApplied.discountType === "percentage" &&
                          ` (${selectedAccommodation.pricing.promoCodeApplied.discountPercentage}% off)`}
                      </p>
                    </div>
                    <p className="accommodation-pricing__value accommodation-pricing__value--discount">
                      -{selectedAccommodation.pricing.currency}{" "}
                      {selectedAccommodation.pricing.promoCodeApplied.calculatedDiscount.toLocaleString()}
                    </p>
                  </div>
                )}
                <div className="accommodation-pricing__row accommodation-pricing__row--total">
                  <p className="accommodation-pricing__label">Total Amount</p>
                  <p className="accommodation-pricing__value">
                    {selectedAccommodation.pricing.currency}{" "}
                    {selectedAccommodation.pricing.totalAfterDiscount.toLocaleString()}
                  </p>
                </div>
              </div>
            </section>

            {selectedAccommodation.teamMemberFeeDetails && (
              <section className="accommodation-detail__section">
                <h3 className="accommodation-detail__title">Registration Fee</h3>
                <div className="accommodation-fee">
                  <div className="accommodation-fee__row">
                    <p className="accommodation-fee__label">
                      {selectedAccommodation.teamMemberFeeDetails.registrationFee.description}
                    </p>
                    <p className="accommodation-fee__value">
                      {selectedAccommodation.teamMemberFeeDetails.registrationFee.currency}{" "}
                      {selectedAccommodation.teamMemberFeeDetails.registrationFee.amount.toLocaleString()}
                      {selectedAccommodation.teamMemberFeeDetails.registrationFee.perTeam ? " (per team)" : " (per player)"}
                    </p>
                  </div>
                  {selectedAccommodation.teamMemberFeeDetails.registrationFee.details &&
                    selectedAccommodation.teamMemberFeeDetails.registrationFee.details.length > 0 && (
                      <div className="accommodation-fee__details">
                        <p className="accommodation-fee__details-label">Includes:</p>
                        <ul>
                          {selectedAccommodation.teamMemberFeeDetails.registrationFee.details.map((detail, idx) => (
                            <li key={idx}>{detail}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                </div>
              </section>
            )}

            <p className="accommodation-detail__booked">
              Booked on {moment(selectedAccommodation.createdAt).format("DD MMM YYYY, HH:mm")}
            </p>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default TournamentAccommodations;
